"use client";

import { useState } from "react";
import Image from "next/image";
import { ArrowRight, ImageIcon } from "lucide-react";
import { CtaButton } from "@/components/layout/CtaButton";

export type FlipCardProps = {
  imageSrc: string;
  imageAlt: string;
  title: string;
  author?: string;
  description?: string;
  exploreHref?: string;
  exploreLabel?: string;
  className?: string;
  variant?: "flip" | "zoom";
};

export function FlipCard({
  imageSrc,
  imageAlt,
  title,
  author,
  description,
  exploreHref = "#lead",
  exploreLabel = "Explore",
  className,
  variant = "flip",
}: FlipCardProps) {
  const [flipped, setFlipped] = useState(false);
  const [imgError, setImgError] = useState(false);
  const isZoomCard = variant === "zoom";

  return (
    <div
      className={[
        "group h-72 w-full sm:h-80",
        isZoomCard ? "overflow-hidden rounded-xl" : "perspective-[1000px]",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      onMouseEnter={() => {
        if (!isZoomCard) {
          setFlipped(true);
        }
      }}
      onMouseLeave={() => {
        if (!isZoomCard) {
          setFlipped(false);
        }
      }}
      onClick={() => {
        if (!isZoomCard) {
          setFlipped((prev) => !prev);
        }
      }}
      onKeyDown={(event) => {
        if (!isZoomCard && (event.key === "Enter" || event.key === " ")) {
          event.preventDefault();
          setFlipped((prev) => !prev);
        }
      }}
      role={isZoomCard ? undefined : "button"}
      tabIndex={isZoomCard ? undefined : 0}
      aria-pressed={isZoomCard ? undefined : flipped}
      aria-label={
        isZoomCard ? undefined : `${title}. ${flipped ? "Showing details" : "Show details"}`
      }
    >
      <div
        className={[
          "relative h-full w-full transition-transform duration-300 ease-out",
          isZoomCard ? "group-hover:scale-[1.03]" : "transform-3d duration-[900ms]",
          !isZoomCard && flipped ? "transform-[rotateY(180deg)]" : "",
        ].join(" ")}
      >
        {/* Front */}
        <div
          className={[
            "absolute inset-0 overflow-hidden rounded-xl shadow-lg",
            isZoomCard ? "" : "backface-hidden",
          ].join(" ")}
        >
          {imgError || !imageSrc ? (
            <div className="flex h-full w-full flex-col items-center justify-center bg-[#1f2914] px-3 text-center text-gray-400">
              <ImageIcon className="h-10 w-10" aria-hidden="true" />
              <span className="mt-2 text-xs font-medium text-white/80">
                {title}
              </span>
            </div>
          ) : (
            <Image
              src={imageSrc}
              alt={imageAlt}
              fill
              sizes="(max-width: 640px) 50vw, (max-width: 1024px) 20vw, 16vw"
              loading="lazy"
              fetchPriority="low"
              className="object-cover"
              onError={() => setImgError(true)}
            />
          )}
        </div>

        {!isZoomCard ? (
          <div className="absolute inset-0 overflow-hidden rounded-xl shadow-lg backface-hidden transform-[rotateY(180deg)]">
            <div className="absolute inset-0">
              {imgError || !imageSrc ? (
                <div className="h-full w-full bg-[#1f2914]" />
              ) : (
                <Image
                  src={imageSrc}
                  alt={imageAlt}
                  fill
                  sizes="(max-width: 640px) 50vw, (max-width: 1024px) 20vw, 16vw"
                  loading="lazy"
                  fetchPriority="low"
                  className="object-cover"
                  onError={() => setImgError(true)}
                />
              )}
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/70 to-black/85" />
              <div className="absolute inset-0 bg-[#1f2914]/20 backdrop-blur-[1px]" />
            </div>

            <div className="relative flex h-full flex-col justify-end px-4 py-5 text-center">
              <div className="mb-5 space-y-2 rounded-2xl border border-white/10 bg-black/20 px-3 py-4 backdrop-blur-sm">
                <h3 className="font-heading text-sm font-bold uppercase tracking-[0.18em] text-white sm:text-base">
                  {title}
                </h3>
                {author ? (
                  <p className="text-xs font-medium uppercase tracking-[0.18em] text-white/85 sm:text-sm">
                    {author}
                  </p>
                ) : description ? (
                  <p className="line-clamp-4 text-xs leading-relaxed text-white/85">
                    {description}
                  </p>
                ) : null}
              </div>
              <div className="mx-auto" onClick={(event) => event.stopPropagation()}>
                <CtaButton
                  href={exploreHref}
                  variant="green"
                  size="sm"
                  icon={<ArrowRight className="h-3.5 w-3.5" />}
                  iconPosition="right"
                >
                  {exploreLabel}
                </CtaButton>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
